/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Output,
	effect,
	forwardRef,
	input,
	signal,
	viewChild
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { PHONE_PREFIXES } from "@app/constants/phone-prefixes.const";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { InputComponent } from "../input/input.component";

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => InputPhoneComponent),
	multi: true,
};

@Component({
	selector: "cb-input-phone",
	standalone: true,
	imports: [
		FormsModule,
		TooltipModule,
		InputComponent,
	],
	templateUrl: "./input-phone.component.html",
	styleUrl: "./input-phone.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputPhoneComponent implements ControlValueAccessor {
	public readonly prefixOptions = PHONE_PREFIXES.sort(
		(a, b) => Number.parseInt(a.dial_code) - Number.parseInt(b.dial_code),
	).map((prefix) => ({
		label: `${prefix.emoji} +${prefix.dial_code}`,
		value: `+${prefix.dial_code}`,
	}));

	/**
	 * The default prefix of the phone number
	 */
	public readonly defaultPrefix = input("+39");

	/**
	 * Placeholder of the input
	 */
	public readonly placeholder = input<string>();

	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input(false);

	/**
	 * Leading icon of the input
	 */
	public readonly icon = input<string>();

	/**
	 * Whether the input should have a copy button
	 */
	public readonly copyButton = input<boolean>();

	/**
	 * Whether the input has an error
	 */
	public readonly error = input<boolean>();

	/**
	 * HTML name standard attribute
	 */
	public readonly name = input<string>();

	/**
	 * Reference to the input element
	 */
	readonly inputElement = viewChild.required<InputComponent>("Input");

	/**
	 * Emits the value of the input on change
	 */
	@Output() public change: EventEmitter<string | null> = new EventEmitter<
		string | null
	>();

	/**
	 * The internal data model
	 */
	//private innerValue: string | null = "";
	protected value = signal<string | null>("");

	constructor() {
		effect(() => {
			this.onChangeCallback(this.value());
			this.change.emit(this.value());
		});
	}

	public writeValue(obj: string | null): void {
		if (obj !== this.value()) {
			let value = obj;
			// If the value is null or it does not start with +, set the default prefix
			if (!value) {
				value = this.defaultPrefix();
			} else if (!value.startsWith("+")) {
				value = this.defaultPrefix() + value;
			}

			this.value.set(value);
		}
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	protected onChange() {
		this.change.emit(this.value());
	}
}
